import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { PricingGridWrapper } from '@cimpress-technology/pricing-grid';
import { Tooltip } from '@cimpress/react-components';
import { auth } from '../../../utils/auth';
import { isProduction } from '../../../utilities/processFunctions';
import { css } from 'emotion';
import { SkuInfoShape } from '../createContract/shared/SkuCard/SkuCard';
import { IsProductTermType, IsShippingTermType, TermType } from '../../../constants/termTypes';
import { selectors as termSelectors } from '../../../reducers/terms';
import PricingGrid from '@cimpress-technology/pricing-grid-ts';
import UnionSelector from '@cimpress-technology/union-selector';
import cloneDeep from 'lodash/cloneDeep';
import { connect } from 'react-redux';

const pricingGridContainerStyle = css`
  display: flex;
`;

const attributeSelectorStyle = css`
  max-width: 50%;
  height: 650px;
  overflow-y: auto;
  padding-right: 20px;
  margin-right: 20px;
`;
class TermPricingGrid extends Component {
  static defaultProps = {};

  static propTypes = {
    termRevision: PropTypes.object.isRequired,
    skuInfo: SkuInfoShape.isRequired,
    priceModelInfo: PropTypes.object,
    title: PropTypes.node,
    columnTitle: PropTypes.string
  };

  state = {};

  onChange = productState => {
    const { skuInfo } = this.props;
    const sku = skuInfo && skuInfo.sku;
    let newProductState = {};

    if (productState[sku]) {
      newProductState[sku] = productState[sku];
    }
    const newProductStateValues = Object.values(newProductState);

    if (newProductStateValues && newProductStateValues.length > 0) {
      const { displayedAttributes } = newProductStateValues[0];
      this.setState({
        productState: newProductState,
        initialDisplayedAttributes: displayedAttributes
      });
    } else {
      this.setState({
        productState: newProductState,
        initialDisplayedAttributes: {}
      });
    }
  };

  onError = (errormessage, x) => {
    console.log(errormessage);
  };

  getShippingContractInfo = () => {
    const { termRevision } = this.props;

    const destinationCountries = get(termRevision, 'destinationCountries', []);
    const discountPercent = get(termRevision, 'modifiers.discountPercent', null);
    const markupPercent = get(termRevision, 'modifiers.markupPercent', null);

    let productPrice = [];
    let shippingCostPreview = {};

    shippingCostPreview = {
      title: get(termRevision, 'displayAgreementType', '') || 'Contract Shipping Cost',
      discountPercent,
      markupPercent,
      destinationCountries: destinationCountries.length > 0 ? destinationCountries : undefined,
      isFreeShipping: termRevision.type === TermType.FREE_SHIPPING
    };
    return { productPrice, shippingCostPreview };
  };

  formProductPriceContractsInfo = () => {
    const { termRevision, skuInfo, columnTitle } = this.props;
    const productPrice = [
      {
        buyer: {
          buyerType: get(termRevision, 'buyer.type'),
          buyerId: get(termRevision, 'buyer.id')
        },
        seller: {
          sellerType: get(termRevision, 'seller.type'),
          sellerId: get(termRevision, 'seller.id')
        },
        priceModelId: get(skuInfo, 'priceModel.id'),
        revisionId: get(skuInfo, 'priceModel.revisionId'),
        effectiveStartDate: get(termRevision, 'effectiveStartDate', null),
        contractId: get(termRevision, 'termId'),
        contractRevisionId: get(termRevision, 'revisionId'),
        modifiers: {
          discountPercent: get(termRevision, 'modifiers.discountPercent', 0)
        },
        title: columnTitle || get(termRevision, 'displayAgreementType', '') || 'Contract Price'
      }
    ];
    return { productPrice };
  };

  render() {
    const { termRevision, skuInfo, priceModelInfo, title, skuRuleSet, requiredInputs } = this.props;
    let { productState } = this.state;
    if (!productState) {
      productState = {};
    }

    if (!skuInfo || (IsProductTermType(termRevision.type) && !priceModelInfo)) {
      return null;
    }

    const { sku } = skuInfo;
    let skuData = {};
    if (productState && productState[sku] && productState[sku].selectedRow) {
      skuData = {};
      skuData[sku] = cloneDeep(productState[sku]) || {}; // use deep copy
      skuData[sku] = {
        ...skuData[sku],
        sellerAccountId: termRevision.seller.accountId,
        sellerId: termRevision.seller.id
      };
    }
    let productPriceContractsInfo;
    const isShippingTermType = IsShippingTermType(termRevision.type);
    const sellerType = get(termRevision, 'seller.type');
    const sellerId = get(termRevision, 'seller.id');
    let priceModelUrl = null;
    if (isShippingTermType) {
      productPriceContractsInfo = this.getShippingContractInfo();
    } else if (IsProductTermType(termRevision.type)) {
      productPriceContractsInfo = this.formProductPriceContractsInfo();
      priceModelUrl = `${process.env.REACT_APP_PRICINGMANAGER_URL}/models/${priceModelInfo.id}?revision=${
        priceModelInfo.revisionId
      }`;
    }

    const hasMultipleCurrencies =
      priceModelInfo && priceModelInfo.possibleCurrencies && priceModelInfo.possibleCurrencies.length > 1;

    let multipleCurrencyMsg;

    if (hasMultipleCurrencies) {
      const multipleCurrencyTooltipMsg = (
        <div>
          Use the attribute selector below to change the destination country and see the different currency/prices.
        </div>
      );

      multipleCurrencyMsg = hasMultipleCurrencies && (
        <Tooltip contents={multipleCurrencyTooltipMsg}>
          <p>
            <small>
              <code>
                * The pricing and currency for this product differs by destination country{' '}
                <i className="fa fa-question-circle" />
              </code>
            </small>
          </p>
        </Tooltip>
      );
    }
    let gridToShow;
    if (isShippingTermType) {
      gridToShow = (
        <PricingGridWrapper
          sellerType={sellerType}
          sellerId={sellerId}
          sku={sku}
          onGetAccessToken={auth.getAccessToken}
          productPricePreviews={productPriceContractsInfo.productPrice}
          shippingCostPreview={productPriceContractsInfo.shippingCostPreview}
          isProduction={isProduction}
          attributeSelectorClassname={attributeSelectorStyle}
          gridContainerClassname={pricingGridContainerStyle}
        />
      );
    } else {
      gridToShow =
        skuRuleSet && skuData && requiredInputs ? (
          <div>
            <div className={`col-xs-12 col-md-5`}>
              <UnionSelector
                productId={skuRuleSet.referenceId}
                showErrorModal
                authToken={auth.getAccessToken()}
                consumerId="price"
                showPivotConfig={false}
                onChange={this.onChange}
                showCompetitivePricing={false}
                competitorProducts={[]}
                onError={this.onError}
                requiredAttributes={requiredInputs[skuRuleSet.referenceId]}
              />
            </div>
            <div className={`col-xs-12 col-md-7`}>
              <PricingGrid
                skuData={skuData}
                onGetAccessToken={auth.getAccessToken}
                isProduction={isProduction}
                parentService="contracts"
                widthOfGrid="100%"
                productPriceContractsInfo={productPriceContractsInfo.productPrice}
              />
            </div>
          </div>
        ) : null;
    }
    return (
      <div>
        <div>
          <h5 style={{ display: 'inline-block' }}>
            {title || `Contract Pricing for ${sku} ${priceModelUrl ? '|' : ''}`}
          </h5>
          {priceModelUrl && (
            <a target="_blank" rel="noopener noreferrer" href={priceModelUrl}>
              <span style={{ fontSize: '12px', marginLeft: '6px' }}>
                View in Pricing Manager <i className="fa fa-external-link" />
              </span>
            </a>
          )}
        </div>
        {multipleCurrencyMsg}
        {gridToShow}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    requiredInputs: termSelectors.getTermItemsRequiredInputs(state)
  };
};

export default connect(mapStateToProps)(TermPricingGrid);
